/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState, useContext } from 'react';
import { ClickAwayListener, Popper, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { bool, object, string } from 'prop-types';
import { getFeatureFlag, getPriceRangeLayout } from '../../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import findContent from '../../../../../../../../gql/queries/findContent';
import PriceBreakdownDetail from './PriceBreakdownDetail';
import { trackEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getCurrentPageType } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

const useStyles = makeStyles(() => ({
    container: {
        position: 'relative',

    },
    wrapperRight: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: '16px',
        '& $dialog': {
            left: 'unset',
            right: '16px',
            '@media screen and (max-width: 440px)': {
                width: '100%',
            },
        },
    },
    messageCopy: {
        color: '#434343',
        fontSize: '14px',
        lineHeight: '20px ',
        letterSpacing: '0.5px',
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
        paddingBottom: '5px',
        '@media screen and (max-width: 768px)': {
            fontSize: '12px',
        },
    },
    popperRoot: {
        zIndex: 9999,
    },
    wrapperPDP: {
        paddingRight: '0px',
    },
    messageHierarchyCopy: {
        fontSize: '14px',
        lineHeight: '18px',
        paddingBottom: '5px',
        letterSpacing: 'normal',
        color: '#555555',
        '@media only screen and (max-width: 768px)': {
            lineHeight: '16px !important',
            fontSize: '12px !important',
        },
    },
    wrapUpRedesignCopy: {
        lineHeight: '19px',
        '@media only screen and (max-width: 768px)': {
            lineHeight: '17px',
        },
    },
}));

const PriceBreakDownUi = ({
    popperPosition, dynamicPriceData, isPdpRedesignEnabled,
    addOnPrice, isCaDelivery, customClass,
}) => {
    const classes = useStyles();
    const pageType = useSelector(getCurrentPageType);
    const [isOpen, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const dispatch = useDispatch();
    const ffEnabled = useSelector(getFeatureFlag('is-price-transparency-enabled'));

    const openPriceDialog = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setAnchorEl(event?.currentTarget);
        setOpen(!isOpen);
    };

    const priceRangeLayout = useSelector(getPriceRangeLayout);
    const productType = dynamicPriceData?.productType?.toLowerCase?.() === 'fpt' ? 'fpt' : 'standard';
    const linkTextEnabled = ffEnabled?.[productType]?.link;
    const priceBreakdownModelEnabled = ffEnabled?.[productType]?.model;
    const isDynamicEnabled = (ffEnabled?.[productType] && (linkTextEnabled || priceBreakdownModelEnabled) && dynamicPriceData?.prices?.[0]?.displayCharges) || false;

    useEffect(() => {
        if (typeof document !== 'undefined' && ffEnabled && dynamicPriceData) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            if (anchorEl) {
                document.body.setAttribute('style', `overflow: hidden; padding-right: ${scrollbarWidth}px;`);
            } else {
                document.body.setAttribute('style', 'overflow: auto;');
            }
        }
        return () => {
            if (typeof document !== 'undefined') {
                document.body.style.overflow = 'auto';
                document.body.style.paddingRight = '';
            }
        };
    }, [anchorEl]);

    const findContentOptions = {
        brand: 'global',
        contentType: 'ca_product_messaging',
    };

    /**
     * Data will ge get from Apollo cache! It won't query multiple times
     */
    const PRICE_MESSAGE_INFORMATION = findContent(findContentOptions);
    const { data, loading, error } = useQuery(PRICE_MESSAGE_INFORMATION);
    const cmsPricingData = data?.findContent?.content?.entries || [];
    const productDeliveryType = productType === 'fpt' ? 'fpt' : 'Standard';

    const priceMessaging = (cmsPricingData?.[0]?.brand || []).find((item) => item.brand_id === dynamicPriceData?.brand) || {};

    const getDetails = () => priceMessaging?.[productDeliveryType.toLowerCase()] || {};

    const productMessageCopy = getDetails().product_message || '';
    useEffect(() => {
        const caDeliverFee = sessionStorage.getItem('caDeliveryFee');
        if (!caDeliverFee && (pageType === 'product' || pageType === 'category')) {
            dispatch(trackEvent({
                eventCategory: 'Experiment',
                eventAction: pageType,
                eventLabel: isCaDelivery ? 'Variant' : 'Control',
                eventName: 'ca_delivery_fee',
                queued: !!(typeof window !== 'undefined' && !window.utag?.data),
                nonInteraction: true,
            }));
            sessionStorage.setItem('caDeliveryFee', true);
        }
    }, [isCaDelivery]);

    useEffect(() => {
        if (isDynamicEnabled && productMessageCopy) {
            const pricingValue = sessionStorage.getItem('pricingUI');
            if (!pricingValue) {
                dispatch(trackEvent({
                    eventCategory: 'Test Impression',
                    eventAction: 'Honest Pricing UI',
                    eventLabel: 'CA',
                    eventName: 'test_impression',
                    queued: !!(typeof window !== 'undefined' && !window.utag?.data),
                    nonInteraction: true,
                }));

                dispatch(emitCustomTrackingEvent({
                    eventType: 'custom',
                    eventSubType: 'custom',
                    action: 'Impression',
                    category: 'Price Transparency Law',
                    label: 'CA',
                }));
                sessionStorage.setItem('pricingUI', true);
            }
        }
    }, [isDynamicEnabled, productMessageCopy]);

    if (loading) {
        return <></>;
    }

    if (error && !data && !loading) {
        return <></>;
    }

    if (!isDynamicEnabled) {
        return <></>;
    }

    if (!ffEnabled || !dynamicPriceData || !productMessageCopy) {
        return <></>;
    }
    let placement = '';
    switch (popperPosition) {
        case 'right':
            placement = 'bottom-end';
            break;
        default:
            placement = 'bottom-start';
            break;
    }
    const productMessageCopyTitle = () => (
        <div role="presentation" aria-label="Click here to open popper" onClick={(e) => openPriceDialog(e)} className={`${addOnPrice ? classes.wrapUpRedesignCopy : ''} ${classes.messageCopy}`}>
            {productMessageCopy}
        </div>
    );

    return (
        <div className={`${classes.container} ${popperPosition === 'right' ? `${classes.wrapperRight} ${isPdpRedesignEnabled ? classes.wrapperPDP : ''}` : ''} ${customClass}`}>
            {!isCaDelivery && productMessageCopyTitle()}
            {isOpen && priceBreakdownModelEnabled
                ? (
                    <div
                        className={`${classes.container}`}
                        role="presentation"
                        onClick={(e) => {
                            if (isOpen) {
                                e.stopPropagation();
                            }
                        }}
                        onMouseDown={(e) => {
                            if (isOpen) {
                                e.stopPropagation();
                            }
                        }}
                    >
                        <ClickAwayListener
                            mouseEvent="onMouseDown"
                            touchEvent="onTouchStart"
                            onClickAway={() => {
                                setOpen(false);
                                if (typeof document !== 'undefined') {
                                    document.body.removeAttribute('style', 'overflow');
                                }
                                setAnchorEl(null);
                            }}
                        >
                            <Popper
                                className={classes.popperRoot}
                                open={isOpen}
                                anchorEl={anchorEl}
                                placement={placement}
                                transition
                            >
                                <PriceBreakdownDetail
                                    dynamicPriceData={dynamicPriceData}
                                    priceMessaging={priceMessaging}
                                    productDeliveryType={productDeliveryType}
                                    priceRangeLayout={priceRangeLayout}
                                    openPriceDialog={openPriceDialog}
                                    showSinglePrice={false}
                                />
                            </Popper>
                        </ClickAwayListener>
                    </div>
                )
                : <></>}
        </div>

    );
};

PriceBreakDownUi.propTypes = {
    dynamicPriceData: object.isRequired,
    popperPosition: string,
    isPdpRedesignEnabled: bool,
    customClass: string,
    addOnPrice: bool,
    isCaDelivery: bool,
};
PriceBreakDownUi.defaultProps = {
    popperPosition: '',
    isPdpRedesignEnabled: false,
    customClass: '',
    addOnPrice: false,
    isCaDelivery: false,
};
export default PriceBreakDownUi;
